/**
 * Staff Delivery Exception Review tab.
 *
 * Visible to admin, prescriber, customer_service. Default view = pending
 * queue (the triage list). Filters: Pending / Approved / Denied / All.
 * Approve or Deny inline with an optional staff note. Decision triggers an
 * automatic email to the patient via the backend.
 */
import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plane, RefreshCw, CheckCircle2, XCircle, Clock, Calendar, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "denied", label: "Denied" },
  { key: "", label: "All" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso + "T12:00:00Z").toLocaleDateString(undefined, {
      weekday: "short", month: "short", day: "numeric", year: "numeric",
    });
  } catch { return iso; }
}

function fmtAgo(iso) {
  if (!iso) return "—";
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const hrs = Math.floor(diffMs / 3600000);
    if (hrs < 1) return "just now";
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  } catch { return iso; }
}

export default function DeliveryExceptionsAdminTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [submittingId, setSubmittingId] = useState("");
  // Per-row staff-note draft state so multiple reviewers' typing doesn't collide
  const [noteDrafts, setNoteDrafts] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter ? `/admin/delivery-exceptions?status=${filter}` : "/admin/delivery-exceptions";
      const { data } = await API.get(url);
      setItems(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load requests");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const review = async (id, decision) => {
    setSubmittingId(`${decision}-${id}`);
    try {
      const note = noteDrafts[id] || "";
      await API.put(`/admin/delivery-exceptions/${id}/review`, { decision, staff_note: note });
      toast.success(`Request ${decision === "approve" ? "approved" : "denied"} · patient emailed`);
      setNoteDrafts((d) => ({ ...d, [id]: "" }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not review");
    } finally {
      setSubmittingId("");
    }
  };

  return (
    <Card data-testid="delivery-exceptions-admin-tab">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 to-violet-600 flex items-center justify-center text-white">
            <Plane className="w-4 h-4" />
          </span>
          Delivery Window Requests
          <span className="ml-2 text-xs font-normal text-slate-500">
            Patient-requested 3-day delivery exceptions
          </span>
          <Button size="sm" variant="ghost" onClick={load} disabled={loading} className="ml-auto h-7 px-2">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          {FILTERS.map((f) => (
            <Button
              key={f.key}
              size="sm"
              variant={filter === f.key ? "default" : "outline"}
              onClick={() => setFilter(f.key)}
              className={`h-7 text-xs ${filter === f.key ? "bg-purple-600 hover:bg-purple-700 text-white" : ""}`}
              data-testid={`delivery-exception-filter-${f.label}`}
            >
              {f.label}
            </Button>
          ))}
        </div>

        {loading ? (
          <p className="text-xs text-slate-400 py-6 text-center">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-xs text-slate-400 py-6 text-center italic" data-testid="delivery-exceptions-empty">
            No {filter || "delivery window"} requests right now.
          </p>
        ) : (
          <div className="space-y-2.5" data-testid="delivery-exceptions-list">
            {items.map((it) => (
              <RequestRow
                key={it.id}
                item={it}
                noteDraft={noteDrafts[it.id] || ""}
                onNoteChange={(v) => setNoteDrafts((d) => ({ ...d, [it.id]: v }))}
                onReview={review}
                submittingId={submittingId}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RequestRow({ item, noteDraft, onNoteChange, onReview, submittingId }) {
  const isPending = item.status === "pending";
  const StatusIcon =
    item.status === "approved" ? CheckCircle2
    : item.status === "denied" ? XCircle
    : Clock;
  const statusColor =
    item.status === "approved" ? "text-emerald-600 bg-emerald-50 border-emerald-200"
    : item.status === "denied" ? "text-slate-500 bg-slate-100 border-slate-200"
    : "text-amber-700 bg-amber-50 border-amber-200";

  // Visual flag for windows that are dangerously close — staff should
  // prioritise these because the pharmacy needs the 3-day lead.
  const daysUntilStart = (() => {
    try {
      const start = new Date(item.window_start + "T12:00:00Z");
      return Math.ceil((start.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    } catch { return null; }
  })();
  const urgent = isPending && daysUntilStart !== null && daysUntilStart <= 5;

  return (
    <div
      className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-3.5"
      data-testid={`delivery-exception-row-${item.id}`}
    >
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full border px-2 py-0.5 ${statusColor}`}>
          <StatusIcon className="w-2.5 h-2.5" /> {item.status}
        </span>
        {urgent && (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full bg-rose-100 text-rose-700 border border-rose-200 px-2 py-0.5">
            <AlertTriangle className="w-2.5 h-2.5" /> {daysUntilStart}d to window start
          </span>
        )}
        <span className="font-semibold text-sm text-slate-900 dark:text-white">{item.patient_name || "Patient"}</span>
        <span className="text-[11px] text-slate-400 ml-auto">{fmtAgo(item.created_at)}</span>
      </div>

      <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 mb-2">
        <Calendar className="w-3.5 h-3.5 text-purple-600 flex-shrink-0" />
        <span className="font-medium">{fmtDate(item.window_start)} → {fmtDate(item.window_end)}</span>
        <span className="text-xs text-slate-400">({item.window_days} day{item.window_days > 1 ? "s" : ""})</span>
      </div>

      <blockquote className="text-xs text-slate-600 dark:text-slate-300 italic border-l-2 border-purple-300 pl-2 mb-2 leading-relaxed">
        &quot;{item.reason}&quot;
      </blockquote>

      {item.staff_note && (
        <p className="text-xs text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/40 rounded px-2 py-1 mb-2">
          <span className="font-semibold">{item.reviewed_by_name}:</span> {item.staff_note}
        </p>
      )}

      {item.reviewed_at && (
        <p className="text-[10px] text-slate-400 mb-2">
          Reviewed by {item.reviewed_by_name} ({item.reviewed_role}) on {new Date(item.reviewed_at).toLocaleString()}
          {item.consumed_at && ` · Applied to order ${item.consumed_order_id?.slice(0, 8)}…`}
        </p>
      )}

      {isPending && (
        <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800 space-y-2">
          <input
            type="text"
            value={noteDraft}
            onChange={(e) => onNoteChange(e.target.value)}
            placeholder="Note for the patient (optional, included in email)"
            maxLength={500}
            className="w-full text-xs rounded-md border border-slate-200 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-purple-500/30"
            data-testid={`delivery-exception-note-${item.id}`}
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => onReview(item.id, "approve")}
              disabled={submittingId === `approve-${item.id}`}
              className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
              data-testid={`delivery-exception-approve-${item.id}`}
            >
              <CheckCircle2 className="w-3 h-3 mr-1" />
              {submittingId === `approve-${item.id}` ? "Approving…" : "Approve"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onReview(item.id, "deny")}
              disabled={submittingId === `deny-${item.id}`}
              className="h-7 text-xs"
              data-testid={`delivery-exception-deny-${item.id}`}
            >
              <XCircle className="w-3 h-3 mr-1" />
              {submittingId === `deny-${item.id}` ? "Denying…" : "Deny"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
